import { Box, Divider, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useListAgentsQuery } from '../../api/agents.api';
import { useGetCatalogQuery } from '../../api/content.api';
import { AgentSearchResultCard } from '../../components/AgentSearchResultCard';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { FilterSidebar } from '../../components/FilterSidebar';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { ReturnLink } from '../../components/ReturnLink';
import { SearchResultCard } from '../../components/SearchResultCard';
import {
  SDLC_PATH,
  SEARCH_QUERY_PARAM,
  SEARCH_STAGE_PARAM,
  SEARCH_TYPE_PARAM,
} from '../../config';
import {
  buildRecordTrail,
  countLabel,
  dottedReference,
  namesByPath,
  numbersByPath,
  runSearch,
  toggleFacet,
} from '../../content';
import type { SearchResults, SearchType } from '../../content';
import {
  contentWidthSx,
  filterSettleSx,
  pageEnterSx,
  pageGutterSx,
  searchColumnsSx,
  sectionSpacingSx,
  stageNameFor,
} from '../../theme';
import { ContentState } from '../../components/ContentState';

/**
 * `/search` — the catalog, searched in the browser over the cache `SdlcLayout`
 * already holds. See `docs/adr/0002-client-side-search.md`, and `config.ts` for
 * why the whole of this page's state is the URL.
 *
 * **Outside `SdlcLayout`**, so it owns its gutters and its own trail: there is
 * no lifecycle rail here, because a result set spans stages by design and a rail
 * would highlight nothing.
 */
export const SearchPage = () => {
  const [params, setParams] = useSearchParams();
  const { data: catalog, isLoading, error, refetch } = useGetCatalogQuery();
  // **A secondary query, and its failure is silent**: the page is about the catalog,
  // so an agent list that cannot be read costs the agent results and nothing else,
  // where a banner over a working search would cost more than it reports.
  const { data: agents } = useListAgentsQuery();

  const query = params.get(SEARCH_QUERY_PARAM) ?? '';

  // Memoised on `params`, which is stable between navigations. `getAll` returns
  // a new array every render, so a filter object built inline is never
  // referentially equal to the last and the memo below would never hit.
  const filters = useMemo(
    () => ({
      stages: params.getAll(SEARCH_STAGE_PARAM),
      types: params.getAll(SEARCH_TYPE_PARAM) as SearchType[],
    }),
    [params],
  );

  const results = useMemo(
    () => runSearch(catalog ?? [], query, filters, stageNameFor, agents ?? []),
    [catalog, query, filters, agents],
  );

  /**
   * What the results column is keyed on, so it replays its settle when the
   * filters change and sits still otherwise. **Sorted**, so it is the set that
   * matters and not the order it was built in — a signature that changes when
   * nothing has is an animation firing for no reason.
   */
  const filterSignature = `${[...filters.stages].sort().join(',')}|${[...filters.types].sort().join(',')}`;

  // One pass over the catalog for every card's trail and reference, rather than
  // one per card per level.
  const names = useMemo(() => namesByPath(catalog ?? []), [catalog]);
  const numbers = useMemo(() => numbersByPath(catalog ?? []), [catalog]);

  /** Rewrites one repeated parameter, keeping everything else in the URL. */
  const setGroup = (key: string, values: string[]) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    for (const value of values) {
      next.append(key, value);
    }
    // Replaces rather than pushes: ticking four boxes should not cost four
    // presses of the back button to undo.
    setParams(next, { replace: true });
  };

  return (
    <Box sx={{ ...pageGutterSx, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ ...contentWidthSx, flex: 1, display: 'flex', flexDirection: 'column', py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ReturnLink to={SDLC_PATH} label="the toolkit" />
          <Breadcrumbs crumbs={[{ label: 'SDLC', to: SDLC_PATH }, { label: 'Search' }]} />
        </Box>

        <Box
          sx={{
            marginTop: 6,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          <Box>
            <Typography variant="h1" component="h1">
              Search Results
            </Typography>
            {query !== '' && (
              <Typography variant="body1" sx={{ color: 'text.meta', marginTop: 2 }}>
                Query: <Box component="strong" sx={{ color: 'text.primary' }}>&ldquo;{query}&rdquo;</Box>
              </Typography>
            )}
          </Box>

          {/*
            **Hidden until there is an answer**, which is three cases and not
            one: nothing asked yet, catalog still arriving, and read failed. All
            three would otherwise print "Showing 0 matching items" over a
            skeleton or an error, reading as a search that found nothing.
          */}
          {!results.isUnasked && !isLoading && error === undefined && (
            <Typography variant="body2" sx={{ color: 'text.meta' }}>
              Showing {countLabel(results.items.length + results.agents.length, 'matching item')} in the delivery taxonomy
            </Typography>
          )}
        </Box>

        <Divider sx={{ marginTop: 6 }} />

        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => {
            void refetch();
          }}
          errorTitle="Unable to search the toolkit"
          errorNoun="catalog"
          skeleton={<LoadingSkeleton layout="search" label="Loading the catalog" />}
        >
          {/*
            **Filters first in the source, results second.** The grid's named
            areas swap them on desktop; below it the source order is the reading
            order — see `searchColumnsSx`.

            **Keyed on the query and not on the whole URL**, which is what keeps
            a new search reading as a new page while a filter tick stays instant.
            The heading, trail and query line are chrome and stay put.
          */}
          <Box key={query} sx={{ ...searchColumnsSx, ...sectionSpacingSx, ...pageEnterSx }}>
            <Box sx={{ gridArea: 'filters' }}>
              <FilterSidebar
                groups={[
                  {
                    legend: 'Lifecycle Stage',
                    options: results.stages,
                    onToggle: (value) => {
                      setGroup(SEARCH_STAGE_PARAM, toggleFacet(filters.stages, value));
                    },
                  },
                  {
                    legend: 'Artifact Type',
                    options: results.types,
                    onToggle: (value) => {
                      setGroup(SEARCH_TYPE_PARAM, toggleFacet(filters.types, value as SearchType));
                    },
                  },
                ]}
                onReset={
                  filters.stages.length === 0 && filters.types.length === 0
                    ? undefined
                    : () => {
                        const next = new URLSearchParams(params);
                        next.delete(SEARCH_STAGE_PARAM);
                        next.delete(SEARCH_TYPE_PARAM);
                        setParams(next, { replace: true });
                      }
                }
              />
            </Box>

            <ResultsRegion
              signature={filterSignature}
              results={results}
              names={names}
              numbers={numbers}
            />
          </Box>
        </ContentState>
      </Box>
    </Box>
  );
};

/**
 * The results column, and the one place that decides which animation a change
 * gets.
 *
 * **The two events must not both fire.** A new search gets the 400ms fade and
 * scale from the block above; a filter tick gets the 180ms settle. Keying on the
 * filters alone is not enough — the query-keyed container remounts this subtree,
 * so the settle would run nested inside the page transition and dim its first
 * frames.
 *
 * **`mountedWith` tells the two apart for free.** This lives *inside* that
 * container, so "has the signature changed since mount" means exactly "has a
 * filter been touched on this search".
 */
const ResultsRegion = ({
  signature,
  results,
  names,
  numbers,
}: {
  signature: string;
  results: SearchResults;
  names: ReadonlyMap<string, string>;
  numbers: ReadonlyMap<string, number>;
}) => {
  const [mountedWith] = useState(signature);

  return (
    <Box
      key={signature}
      sx={{ gridArea: 'results', ...(signature === mountedWith ? {} : filterSettleSx) }}
    >
      <ResultsColumn results={results} names={names} numbers={numbers} />
    </Box>
  );
};

/**
 * The results, or the one sentence that stands in for them. **Three states, not
 * interchangeable**: nothing asked, asked and found nothing, and failed — the
 * last already handled by `ContentState` above. Collapsing the first two would
 * tell someone who has typed nothing that their search failed.
 */
const ResultsColumn = ({
  results,
  names,
  numbers,
}: {
  results: SearchResults;
  names: ReadonlyMap<string, string>;
  numbers: ReadonlyMap<string, number>;
}) => {
  if (results.isUnasked) {
    return <Prompt />;
  }

  if (results.items.length === 0 && results.agents.length === 0) {
    return <NoMatches />;
  }

  return (
    <Box
      component="ul"
      aria-label="Search results"
      sx={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {/* Agents first: there are few, and one that matches by name is usually the
          thing being looked for. Each is told apart by its pill. */}
      {results.agents.map((agent) => (
        <AgentSearchResultCard key={agent.name} agent={agent} />
      ))}
      {results.items.map((result) => (
        <SearchResultCard
          key={result.id}
          result={result}
          reference={dottedReference(result.path, numbers)}
          trail={buildRecordTrail(result.path, names)}
        />
      ))}
    </Box>
  );
};

/**
 * Nothing asked yet. **Not the `EmptyState` component**, which says a request
 * came back empty — that is a gap in the catalog and this is not.
 */
const Prompt = () => (
  <Box sx={{ paddingBlock: 12 }}>
    <Typography variant="h3" component="h2">
      Search the toolkit
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 2, maxWidth: 480 }}>
      Type in the bar above to search every phase, process and method in the delivery
      taxonomy, and the agents that help with them — or narrow the catalog with the
      filters beside this.
    </Typography>
  </Box>
);

/** A search that ran and found nothing. A fact about the query, not a fault. */
const NoMatches = () => (
  <Box sx={{ paddingBlock: 12 }}>
    <Typography variant="h3" component="h2">
      No matching content
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 2, maxWidth: 480 }}>
      Nothing in the taxonomy matches that. Try fewer words, or clear a filter — a
      stage or type showing 0 has nothing to add to this search.
    </Typography>
  </Box>
);
